import { Button, Col, Form, Row } from "react-bootstrap";
import UserActionPagesTemplate from "../../Template/UserActionPagesTemplate";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { UserLoginActionService } from "../../Services/UserAction/LoginActionService";
import { StoreProptypes } from "../../PropTypes/StoreProptypes";
import { LOADING, SET_MESSAGE } from "../../Actions/Types";
import { useAuth0 } from "@auth0/auth0-react";

import GoogleIcon from "../../Assets/img/social-icons/google.svg";
import AppleIcon from "../../Assets/img/social-icons/apple.svg";
import MicrosoftIcon from "../../Assets/img/social-icons/microsoft.svg";
import { SocialLoginService } from "../../Services/UserAction/SocialLoginService";
import getBaseURL from "../../Helpers/getBaseURL";

const APIBaseURL = `${process.env.REACT_APP_BASE_URL}`;

const baseURL = getBaseURL();

// login page
const LoginPage = () => {
  // Auth0 Hooks
  const {
    loginWithRedirect,
    user: auth0User,
    isLoading,
    isAuthenticated,
  } = useAuth0();

  // Redux Data
  const { user, websiteList } = useSelector((store: StoreProptypes) => store);

  // Functions
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Woocommerce Redirect Token
  const woocommerceToken = searchParams.get("token");

  //Get app parameter
  const app = searchParams.get("app");

  //Get logout parameter
  const logoutFlag = searchParams.get("logout");

  useEffect(() => {
    if (user && websiteList) {
      //console.info("This useEffect Call...");
      navigate("/front");
    }
  }, [user, websiteList]);

  // States
  const [userEmail, setUserEmail] = useState("");
  const [userPassword, setUserPassword] = useState("");
  //const [isFormAlert, setIsFormAlert] = useState<string>('');
  //const [formAlertMessage, setFormAlertMessage] = useState('');

  // Focus Ref
  const emailRef = useRef<HTMLInputElement | null>(null);
  const passwordRef = useRef<HTMLInputElement | null>(null);

  const focusEmailInput = () => {
    if (emailRef && emailRef.current) {
      emailRef.current.focus();
    }
  };

  const focusPasswordInput = () => {
    if (passwordRef && passwordRef.current) {
      passwordRef.current.focus();
    }
  };

  // function to handle form submit
  const HandleLoginFormSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    //setIsFormAlert('');

    // checks if user email address not exist
    if (userEmail === "") {
      focusEmailInput();

      // dispatch error blank email error message
      dispatch({
        type: SET_MESSAGE,
        payload: {
          toast_message: t("Enter Email and Password", { returnObjects: true }),
          toast_type: "danger",
          onclose: null,
        },
      });
    }
    // check if email address in correct format
    else if (
      new RegExp(/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,15}/gi).test(userEmail) ===
      false
    ) {
      focusEmailInput();
      // dispatch invalid error message
      dispatch({
        type: SET_MESSAGE,
        payload: {
          toast_message: t("valid email msg", { returnObjects: true }),
          toast_type: "danger",
          onclose: null,
        },
      });
    }
    // check if user password is empty
    else if (userPassword === "") {
      focusPasswordInput();
      // dispatch blank password error message
      dispatch({
        type: SET_MESSAGE,
        payload: {
          toast_message: t("blank password msg", { returnObjects: true }),
          toast_type: "danger",
          onclose: null,
        },
      });
    }
    // check if user password length is not between 8 to 25
    else if (userPassword.length < 8 || userPassword.length > 25) {
      focusPasswordInput();
      // dispatch invalid error message
      dispatch({
        type: SET_MESSAGE,
        payload: {
          toast_message: t("valid password msg", { returnObjects: true }),
          toast_type: "danger",
          onclose: null,
        },
      });
    } else {
      // call Login action service with email, password, dispatch function, navigate function, t function and wooCommerce Token as parameter
      UserLoginActionService(
        userEmail,
        userPassword,
        focusEmailInput,
        dispatch,
        navigate,
        t,
        woocommerceToken ? woocommerceToken : ``
      );
    }
  };

  const loginWith = (connection: "google-oauth2" | "apple" | "windowslive") => {
    loginWithRedirect({
      appState: {
        returnTo: `${baseURL}/front/login`, // The URL you want the user to be redirected to
      },
      authorizationParams: {
        connection,
      },
    });
  };

  useEffect(() => {
    console.log("auth0User", auth0User);

    if (auth0User) {
      if (!logoutFlag) {
        if (auth0User.email && auth0User.sub) {
          SocialLoginService(
            auth0User.name ? auth0User.name : "User",
            auth0User.email,
            auth0User.sub,
            dispatch,
            navigate,
            t
          );
        }
      } else {
        setTimeout(() => {
          window.location.reload();
        }, 300);
      }
    }
  }, [auth0User, logoutFlag]);

  useEffect(() => {
    /* if (isLoading) {
            dispatch({
                type: LOADING,
                payload: true,
            });
        } */
  }, [isLoading]);

  return (
    <>
      <UserActionPagesTemplate>
        <>
          <h1>{t("login form title")}</h1>
          <div className="sso-login-buttons">
            <Row md={3}>
              <Col>
                <Button
                  className="google d-flex align-items-center w-100 justify-content-center"
                  size="lg"
                  variant="outline-dark"
                  onClick={() => loginWith("google-oauth2")}
                  aria-label={t("Continue with Google")}
                >
                  <span className="icon-wrapper">
                    <img src={GoogleIcon} alt="" />
                  </span>
                  <span className="text-wrapper">{t("Google")}</span>
                </Button>
              </Col>
              <Col>
                <Button
                  className="microsoft d-flex align-items-center w-100 justify-content-center"
                  size="lg"
                  variant="outline-dark"
                  onClick={() => loginWith("windowslive")}
                  aria-label={t("Sign up with Microsoft")}
                >
                  <span className="icon-wrapper">
                    <img src={MicrosoftIcon} alt="" />
                  </span>
                  <span className="text-wrapper">{t("Microsoft")}</span>
                </Button>
              </Col>
              <Col>
                <Button
                  className="apple d-flex align-items-center w-100 justify-content-center"
                  size="lg"
                  variant="outline-dark"
                  onClick={() => loginWith("apple")}
                  aria-label={t("Sign up with Apple")}
                >
                  <span className="icon-wrapper">
                    <img src={AppleIcon} alt="" />
                  </span>
                  <span className="text-wrapper">{t("Apple")}</span>
                </Button>
              </Col>
            </Row>
          </div>
          <div className="email-login-text-wrapper">
            <div className="divider"></div>
            <div className="email-login-text">
              {t("Or log in with your email")}
            </div>
            <div className="divider"></div>
          </div>

          <Form>
            <Form.Group className="form-group" controlId="user_email">
              <Form.Label>{t("form email filed label")}</Form.Label>
              <Form.Control
                type="email"
                size="lg"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setUserEmail(e.target.value)
                }
                value={userEmail}
                aria-required="true"
                autoComplete="email"
                ref={emailRef}
              />
            </Form.Group>
            <Form.Group className="form-group" controlId="user_password">
              <Form.Label>{t("password placeholder label")}</Form.Label>
              <Form.Control
                type="Password"
                size="lg"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setUserPassword(e.target.value)
                }
                value={userPassword}
                aria-describedby="passwordHelpBlock"
                aria-required="true"
                ref={passwordRef}
              />
              <Form.Text
                as={`div`}
                id="passwordHelpBlock"
                className="aioa_form-password-info-text"
              >
                {t("valid password msg")}
              </Form.Text>
            </Form.Group>
            <Button
              variant="primary"
              type="submit"
              size="lg"
              className="w-100 submit-btn"
              onClick={HandleLoginFormSubmit}
            >
              {t("Login")}
            </Button>
          </Form>

          <div className="aioa_dashboard-login-link">
            <Link
              to={`/front/forgot-password${
                app && app === "readwritemadesimple"
                  ? "?app=readwritemadesimple"
                  : ""
              }`}
              className="aioa_forgot-password-link"
            >
              {" "}
              {t("lost password label")}
            </Link>
            {app && app === "readwritemadesimple" ? (
              <></>
            ) : (
              <a
                href={`${APIBaseURL}/trial-subscription`}
                className="aioa_free-trial-link"
              >
                {t("free trial btn label")}
              </a>
            )}
          </div>
        </>
      </UserActionPagesTemplate>
    </>
  );
};

export default LoginPage;
